// layers.ts
import { RasterLayer, Layer, FieldNumber, InstrumentType, TimePoint } from '../../types';
import { parseDatasets, DatasetsData, getDatasetInfo } from '../../utils/datasetsParse';
import L from 'leaflet';
// Base URL for your tile server
//const BASE_URL = "http://0.0.0.0:8000/data/static_tiles";
const BASE_URL = process.env.REACT_APP_TILE_URL;

// Default zoom levels
const DEFAULT_MIN_ZOOM = parseInt((process.env.REACT_APP_MIN_ZOOM || '18') as string, 10);
const DEFAULT_MAX_ZOOM = parseInt((process.env.REACT_APP_MAX_ZOOM || '28') as string, 10);

// these need to be offset by +1 from our actual folder structure
// we declare zoomOffset: -1 elsewhere, since that's needed to calculate the correct xyz tile
// so these are shifted +1 to account for that
const DEFAULT_NATIVE_MIN_ZOOM = 20;
const DEFAULT_NATIVE_MAX_ZOOM = 27;

// Helper function to construct the URL based on folder path and layer type
const constructTileUrl = (folderPath: string, instrument: InstrumentType): string => {
  // Spectral layers use PNG, all others use WebP
  const extension = instrument === 'Spectral' ? 'png' : 'webp';
  return `${BASE_URL}/${encodeURIComponent(folderPath)}/{z}/{x}/{y}.${extension}`;
};

const field1corner1 = L.latLng(36.3541, -96.8586,);
const field1corner2 = L.latLng(36.3547, -96.8582);
const field1Bounds = L.latLngBounds(field1corner1, field1corner2);

const field2corner1 = L.latLng(36.3528, -96.8573,);
const field2corner2 = L.latLng(36.3532, -96.8567);
const field2Bounds = L.latLngBounds(field2corner1, field2corner2);

const field3corner1 = L.latLng(36.3557, -96.8612,);
const field3corner2 = L.latLng(36.3568, -96.8600);
const field3Bounds = L.latLngBounds(field3corner1, field3corner2);

// Export field bounds so they can be used in other components
export { field1Bounds, field2Bounds, field3Bounds };

// Legacy hardcoded layers - kept for fallback compatibility
export const rasterLayers: RasterLayer[] = [
  // Field 1 layers
  {
    id: "field1-rgb-pre",
    name: "RGB Pre-Burial",
    url: constructTileUrl("3-1 Field1_RGB_PreBurial_ortho_georeferenced_transparent", "Ortho"),
    field: 1,
    instrument: "Ortho",
    timePoint: "PreBurial",
    visible: false,
    opacity: 1.0,
    minZoom: DEFAULT_MIN_ZOOM,
    maxZoom: DEFAULT_MAX_ZOOM,
    minNativeZoom: DEFAULT_NATIVE_MIN_ZOOM,
    maxNativeZoom: DEFAULT_NATIVE_MAX_ZOOM,
    bounds: field1Bounds
  },
  {
    id: "field1-ortho-post",
    name: "RGB Post-Burial",
    url: constructTileUrl("3-2 Field1_June13_2023_Ortho_clipped_georeferenced", "Ortho"),
    field: 1,
    instrument: "Ortho",
    timePoint: "PostBurial",
    date: "June 13, 2023",
    visible: true,
    opacity: 1.0,
    minZoom: DEFAULT_MIN_ZOOM,
    maxZoom: DEFAULT_MAX_ZOOM,
    minNativeZoom: DEFAULT_NATIVE_MIN_ZOOM,
    maxNativeZoom: DEFAULT_NATIVE_MAX_ZOOM,
    //bounds: field1Bounds

  },
  {
    id: "field1-tir-pre",
    name: "TIR Pre-Burial",
    url: constructTileUrl("4-1 Field1_March7th_Preburial_102FTask_TIR_Ortho_clipped", "TIR"),
    field: 1,
    instrument: "TIR",
    timePoint: "PreBurial",
    date: "March 7",
    visible: false,
    opacity: 1.0,
    minZoom: DEFAULT_MIN_ZOOM,
    maxZoom: DEFAULT_MAX_ZOOM,
    minNativeZoom: DEFAULT_NATIVE_MIN_ZOOM,
    maxNativeZoom: DEFAULT_NATIVE_MAX_ZOOM,
    bounds: field1Bounds

  },
  {
    id: "field1-tir-post",
    name: "TIR (1 hour Post-Burial)",
    url: constructTileUrl("4_2_field_1_post_TIR", "TIR"),
    field: 1,
    instrument: "TIR",
    timePoint: "PostBurial",
    visible: false,
    opacity: 1.0,
    minZoom: DEFAULT_MIN_ZOOM,
    maxZoom: DEFAULT_MAX_ZOOM,
    minNativeZoom: DEFAULT_NATIVE_MIN_ZOOM,
    maxNativeZoom: DEFAULT_NATIVE_MAX_ZOOM,
    bounds: field1Bounds

  },
  {
    id: "field1-tir-post-june",
    name: "TIR (3 months Post-Burial)",
    url: constructTileUrl("4-3 Field1_June12th2023_131FTask_TIR_Ortho_clipped", "TIR"),
    field: 1,
    instrument: "TIR",
    timePoint: "PostBurial",
    date: "June 12, 2023",
    visible: false,
    opacity: 1.0,
    minZoom: DEFAULT_MIN_ZOOM,
    maxZoom: DEFAULT_MAX_ZOOM,
    minNativeZoom: DEFAULT_NATIVE_MIN_ZOOM,
    maxNativeZoom: DEFAULT_NATIVE_MAX_ZOOM,
    bounds: field1Bounds

  },

  // Field 2 layers
  {
    id: "field2-tir-june10",
    name: "TIR (June 10, 2024)",
    url: constructTileUrl("18-1 Field2_June10th_2024_TIR_Ortho_georeferenced_clip", "TIR"),
    field: 2,
    instrument: "TIR",
    timePoint: "Other",
    date: "June 10, 2024",
    visible: false,
    opacity: 1.0,
    minZoom: DEFAULT_MIN_ZOOM,
    maxZoom: DEFAULT_MAX_ZOOM,
    minNativeZoom: DEFAULT_NATIVE_MIN_ZOOM,
    maxNativeZoom: DEFAULT_NATIVE_MAX_ZOOM,
    bounds: field2Bounds

  },
  {
    id: "field2-tir-june11",
    name: "TIR (June 11, 2024)",
    url: constructTileUrl("18-2 Field2_June11th_2024_TIR_Ortho_georeferenced_clip", "TIR"),
    field: 2,
    instrument: "TIR",
    timePoint: "Other",
    date: "June 11, 2024",
    visible: false,
    opacity: 1.0,
    minZoom: DEFAULT_MIN_ZOOM,
    maxZoom: DEFAULT_MAX_ZOOM,
    minNativeZoom: DEFAULT_NATIVE_MIN_ZOOM,
    maxNativeZoom: DEFAULT_NATIVE_MAX_ZOOM,
    bounds: field2Bounds
  },
  {
    id: "field2-rgb-post",
    name: "RGB Post-Burial",
    url: constructTileUrl("19-1 RGB_Field2_Post_burial_Ortho_Georeferenced", "Ortho"),
    field: 2,
    instrument: "Ortho",
    timePoint: "PostBurial",
    visible: false,
    opacity: 1.0,
    minZoom: DEFAULT_MIN_ZOOM,
    maxZoom: DEFAULT_MAX_ZOOM,
    minNativeZoom: DEFAULT_NATIVE_MIN_ZOOM,
    maxNativeZoom: DEFAULT_NATIVE_MAX_ZOOM,
    bounds: field2Bounds
  },
  {
    id: "field2-site2-ortho",
    name: "RGB",
    url: constructTileUrl("19-2 Site2_20241011_Autel_orthophoto_clipped_GEOREFERENCED", "Ortho"),
    field: 2,
    instrument: "Ortho",
    timePoint: "Other",
    date: "October 11, 2024",
    visible: false,
    opacity: 1.0,
    minZoom: DEFAULT_MIN_ZOOM,
    maxZoom: DEFAULT_MAX_ZOOM,
    minNativeZoom: DEFAULT_NATIVE_MIN_ZOOM,
    maxNativeZoom: DEFAULT_NATIVE_MAX_ZOOM,
    bounds: field2Bounds
  },
  {
    id: "field2-multispec-green",
    name: "Multispectral (Green)",
    url: constructTileUrl("30 Field2_10m_multispec_green_georef_clip", "Spectral"),
    field: 2,
    instrument: "Spectral",
    timePoint: "Other",
    visible: false,
    opacity: 1.0,
    minZoom: DEFAULT_MIN_ZOOM,
    maxZoom: DEFAULT_MAX_ZOOM,
    minNativeZoom: DEFAULT_NATIVE_MIN_ZOOM,
    maxNativeZoom: DEFAULT_NATIVE_MAX_ZOOM,
    bounds: field2Bounds
  },
  {
    id: "field2-multispec-nir",
    name: "Multispectral (NIR)",
    url: constructTileUrl("30 Field2_10m_multispec_nir_georef_clip", "Spectral"),
    field: 2,
    instrument: "Spectral",
    timePoint: "Other",
    visible: false,
    opacity: 1.0,
    minZoom: DEFAULT_MIN_ZOOM,
    maxZoom: DEFAULT_MAX_ZOOM,
    minNativeZoom: DEFAULT_NATIVE_MIN_ZOOM,
    maxNativeZoom: DEFAULT_NATIVE_MAX_ZOOM,
    bounds: field2Bounds
  },
  {
    id: "field2-multispec-red",
    name: "Multispectral (Red)",
    url: constructTileUrl("30 Field2_10m_multispec_red_georef_clip", "Spectral"),
    field: 2,
    instrument: "Spectral",
    timePoint: "Other",
    visible: false,
    opacity: 1.0,
    minZoom: DEFAULT_MIN_ZOOM,
    maxZoom: DEFAULT_MAX_ZOOM,
    minNativeZoom: DEFAULT_NATIVE_MIN_ZOOM,
    maxNativeZoom: DEFAULT_NATIVE_MAX_ZOOM,
    bounds: field2Bounds
  },
  {
    id: "field2-multispec-reg",
    name: "Multispectral (Registered)",
    url: constructTileUrl("30 Field2_10m_multispec_reg_georef_clip", "Spectral"),
    field: 2,
    instrument: "Spectral",
    timePoint: "Other",
    visible: false,
    opacity: 1.0,
    minZoom: DEFAULT_MIN_ZOOM,
    maxZoom: DEFAULT_MAX_ZOOM,
    minNativeZoom: DEFAULT_NATIVE_MIN_ZOOM,
    maxNativeZoom: DEFAULT_NATIVE_MAX_ZOOM,
    bounds: field2Bounds
  },

  // Field 3 layers
  {
    id: "field3-ortho-pre",
    name: "RGB Pre-Burial",
    url: constructTileUrl("34-1 Field3_Preburial_ortho_georefernced_clip-001", "Ortho"),
    field: 3,
    instrument: "Ortho",
    timePoint: "PreBurial",
    visible: false,
    opacity: 1.0,
    minZoom: DEFAULT_MIN_ZOOM,
    maxZoom: DEFAULT_MAX_ZOOM,
    minNativeZoom: DEFAULT_NATIVE_MIN_ZOOM,
    maxNativeZoom: DEFAULT_NATIVE_MAX_ZOOM,
    bounds: field3Bounds
  },
  {
    id: "field3-ortho-post",
    name: "RGB Post-Burial",
    url: constructTileUrl("34-2 Field3_Postburial_ortho_georefernced_clip-002", "Ortho"),
    field: 3,
    instrument: "Ortho",
    timePoint: "PostBurial",
    visible: false,
    opacity: 1.0,
    minZoom: DEFAULT_MIN_ZOOM,
    maxZoom: DEFAULT_MAX_ZOOM,
    minNativeZoom: DEFAULT_NATIVE_MIN_ZOOM,
    maxNativeZoom: DEFAULT_NATIVE_MAX_ZOOM,
    bounds: field3Bounds

  }
];

/**
 * Maps sensor types to instrument types for backward compatibility
 */
function mapSensorTypeToInstrument(sensorType: string): InstrumentType {
  const sensorLower = sensorType.toLowerCase();
  if (sensorLower.includes('rgb') || sensorLower.includes('camera')) {
    return 'Ortho';
  }
  if (sensorLower.includes('thermal') || sensorLower.includes('tir') || sensorLower.includes('lwir')) {
    return 'TIR';
  }
  if (sensorLower.includes('multispectral') || sensorLower.includes('hyperspectral') || sensorLower.includes('spectral')) {
    return 'Spectral';
  }
  return 'Ortho'; // Default fallback
}

/**
 * Maps burial time to TimePoint for backward compatibility
 */
function mapBurialTimeToTimePoint(burialTime: string): TimePoint {
  const burialLower = burialTime.toLowerCase();
  if (burialLower.includes('preburial') || burialLower.includes('pre-burial')) {
    return 'PreBurial';
  }
  if (burialLower.includes('postburial') || burialLower.includes('post-burial')) {
    return 'PostBurial';
  }
  return 'Other';
}

/**
 * Gets the appropriate bounds for a field
 */
function getFieldBounds(fieldNumber: FieldNumber): L.LatLngBounds {
  switch (fieldNumber) {
    case 1: return field1Bounds;
    case 2: return field2Bounds;
    case 3: return field3Bounds;
    default: return field1Bounds;
  }
}

/**
 * Generates a tile URL based on dataset ID and sensor type
 * Note: This mapping would need to be maintained as new datasets are added
 * In the future, this could come from the CSV or a separate mapping file
 */
function generateTileUrl(datasetId: string, sensorType: string): string {
  // This is a simplified mapping - you'd need to maintain folder name mappings
  const folderMappings: Record<string, string> = {
    '3-1': '3-1 Field1_RGB_PreBurial_ortho_georeferenced_transparent',
    '3-2': '3-2 Field1_June13_2023_Ortho_clipped_georeferenced',
    '4-1': '4-1 Field1_March7th_Preburial_102FTask_TIR_Ortho_clipped',
    '4-2': '4_2_field_1_post_TIR',
    '4-3': '4-3 Field1_June12th2023_131FTask_TIR_Ortho_clipped',
    '18-1': '18-1 Field2_June10th_2024_TIR_Ortho_georeferenced_clip',
    '18-2': '18-2 Field2_June11th_2024_TIR_Ortho_georeferenced_clip',
    '19-1': '19-1 RGB_Field2_Post_burial_Ortho_Georeferenced',
    '19-2': '19-2 Site2_20241011_Autel_orthophoto_clipped_GEOREFERENCED',
    '30-1': '30 Field2_10m_multispec_green_georef_clip',
    '30-2': '30 Field2_10m_multispec_nir_georef_clip',
    '30-3': '30 Field2_10m_multispec_red_georef_clip',
    '30-4': '30 Field2_10m_multispec_reg_georef_clip',
    '34-1': '34-1 Field3_Preburial_ortho_georefernced_clip-001',
    '34-2': '34-2 Field3_Postburial_ortho_georefernced_clip-002'
  };

  const folderPath = folderMappings[datasetId];
  if (!folderPath) {
    console.warn(`No folder mapping found for dataset ${datasetId}`);
    return '';
  }

  const instrument = mapSensorTypeToInstrument(sensorType);
  return constructTileUrl(folderPath, instrument);
}

/**
 * Dynamically generates layers from datasets.csv data
 * @param datasetsData - Parsed data from datasets.csv
 * @returns Array of Layer objects
 */
export function generateLayersFromDatasets(datasetsData: DatasetsData): Layer[] {
  const layers: Layer[] = [];

  Object.keys(datasetsData).forEach(datasetId => {
    const dataset = datasetsData[datasetId];

    // Only create layers for datasets that have some data
    if (!dataset.hasRaster && !dataset.hasVector) {
      return;
    }

    const fieldNumber = dataset.fieldCollected as FieldNumber;
    const bounds = getFieldBounds(fieldNumber);

    const layer: Layer = {
      id: `dataset-${datasetId}`,
      name: `${datasetId} ${dataset.sensorType}`,
      datasetId: datasetId,
      field: fieldNumber,
      sensorType: dataset.sensorType,

      // Data availability
      hasImagery: dataset.hasRaster,
      hasDetections: dataset.hasVector,

      // Default visibility (start with nothing visible)
      showImagery: false,
      showDetections: false,

      // Imagery properties
      tileUrl: dataset.hasRaster ? generateTileUrl(datasetId, dataset.sensorType) : undefined,
      bounds: dataset.hasRaster ? bounds : undefined,
      opacity: 1.0,
      minZoom: DEFAULT_MIN_ZOOM,
      maxZoom: DEFAULT_MAX_ZOOM,
      minNativeZoom: DEFAULT_NATIVE_MIN_ZOOM,
      maxNativeZoom: DEFAULT_NATIVE_MAX_ZOOM,

      // Metadata
      group: dataset.group,
      dateCollected: dataset.dateCollected,
      burialTime: dataset.burialTime,
      collectionMethod: dataset.collectionMethod,
      sensor: dataset.sensor,
      platform: dataset.platform,
      agl: dataset.agl
    };

    layers.push(layer);
  });

  // Sort layers by dataset ID using natural sort order
  layers.sort((a, b) => {
    const parseDatasetId = (id: string) => {
      const match = id.match(/^(\d+)(?:-(\d+))?$/);
      if (match) {
        const main = parseInt(match[1]);
        const sub = match[2] ? parseInt(match[2]) : 0;
        return { main, sub };
      }
      return { main: 0, sub: 0 };
    };

    const aId = parseDatasetId(a.datasetId);
    const bId = parseDatasetId(b.datasetId);
    
    // Compare main numbers first (1, 2, 3, etc.)
    if (aId.main !== bId.main) {
      return aId.main - bId.main;
    }
    
    // If main numbers are equal, compare sub numbers (0 for no sub, then 1, 2, etc.)
    return aId.sub - bId.sub;
  });

  return layers;
}

/**
 * Async function to load datasets and generate layers
 * @returns Promise<Layer[]>
 */
export async function loadLayers(): Promise<Layer[]> {
  try {
    const response = await fetch('/data/datasets.csv');
    if (!response.ok) {
      throw new Error(`Failed to load datasets.csv: ${response.status}`);
    }
    const csvContent = await response.text();
    const datasetsData = parseDatasets(csvContent);
    return generateLayersFromDatasets(datasetsData);
  } catch (error) {
    console.error('Error loading layers from datasets.csv:', error);
    // Fallback to hardcoded layers if CSV loading fails
    return convertRasterLayersToLayers(rasterLayers);
  }
}

/**
 * Converts legacy RasterLayer objects to new Layer objects
 * @param rasterLayers - Array of RasterLayer objects
 * @returns Array of Layer objects
 */
function convertRasterLayersToLayers(rasterLayers: RasterLayer[]): Layer[] {
  return rasterLayers.map(rasterLayer => ({
    id: rasterLayer.id,
    name: rasterLayer.name,
    datasetId: rasterLayer.id.split('-')[0] + (rasterLayer.id.includes('-') ? '-' + rasterLayer.id.split('-')[1] : ''),
    field: rasterLayer.field,
    sensorType: rasterLayer.instrument === 'Ortho' ? 'RGB camera' :
      rasterLayer.instrument === 'TIR' ? 'Thermal camera' : 'Multispectral',

    hasImagery: true,
    hasDetections: true, // Assume all have detections for now

    showImagery: rasterLayer.visible,
    showDetections: false,

    tileUrl: rasterLayer.url,
    bounds: rasterLayer.bounds,
    opacity: rasterLayer.opacity,
    minZoom: rasterLayer.minZoom,
    maxZoom: rasterLayer.maxZoom,
    minNativeZoom: rasterLayer.minNativeZoom,
    maxNativeZoom: rasterLayer.maxNativeZoom,

    group: '',
    dateCollected: rasterLayer.date || '',
    burialTime: rasterLayer.timePoint === 'PreBurial' ? 'Preburial' : 'Postburial',
    collectionMethod: 'Aerial',
    sensor: '',
    platform: '',
    agl: ''
  }));
}

// Helper functions for layers
export const getLayersByField = (layers: Layer[], field: FieldNumber) =>
  layers.filter(layer => layer.field === field);

export const getLayersWithImagery = (layers: Layer[]) =>
  layers.filter(layer => layer.hasImagery);

export const getLayersWithDetections = (layers: Layer[]) =>
  layers.filter(layer => layer.hasDetections);

export const getLayerById = (layers: Layer[], id: string) =>
  layers.find(layer => layer.id === id);

export const getVisibleImageryLayers = (layers: Layer[]) =>
  layers.filter(layer => layer.showImagery && layer.hasImagery);

export const getVisibleDetectionLayers = (layers: Layer[]) =>
  layers.filter(layer => layer.showDetections && layer.hasDetections);

// Legacy helper functions for backward compatibility
export const getLayersByInstrument = (field: FieldNumber, instrument: InstrumentType) => {
  console.warn('getLayersByInstrument is deprecated. Use the new unified Layer system.');
  return rasterLayers.filter(layer => layer.instrument === instrument);
};

export const getLayersByTimePoint = (timePoint: TimePoint) => {
  console.warn('getLayersByTimePoint is deprecated. Use the new unified Layer system.');
  return rasterLayers.filter(layer => layer.timePoint === timePoint);
};

// Legacy function for backward compatibility
export const getLegacyLayerById = (id: string) => {
  console.warn('getLegacyLayerById is deprecated. Use getLayerById with Layer[] instead.');
  return rasterLayers.find(layer => layer.id === id);
};
